<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import { afterUpdate } from 'svelte';

    import StatusIcon from './StatusIcon.svelte';
    import Emoji from '../../_wrappers/Emoji.svelte';
    import { resolveInputStyling } from '../_utils/resolveInputStyling';

    import type { FormInputBinding } from '$lib/types/Components/SharedTypes';
    import type { BrandBorderRadius, Theme } from '$lib/types/Theme';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let finalFormInput: FormInputBinding = {
        name: '',
        value: '',
        onChange: () => {},
        onBlur: () => {},
        onFocus: () => {},
    };
    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';
    export let theme: Theme | undefined = undefined;

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let textareaContainerClasses: string;

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && !pristine;
    });

    const borderRadius = getBorderRadius(
        'inputTextarea',
        theme?.borderRadius as BrandBorderRadius | undefined,
    );

    const { shadowClass, borderColor, backgroundColor, textColor, mutedTextColor } =
        resolveInputStyling({
            theme,
            shadowComponent: 'textareaInput',
        });

    $: textareaContainerClasses = cn(
        'fun-box-border fun-size-full fun-border fun-bg-white fun-p-4 fun-align-middle fun-ring-2 fun-ring-transparent fun-transition-all',
        borderRadius,
        shadowClass,
        {
            ['fun-pl-[52px]']: showEmoji,
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: active,
        },
    );

    const textareaClasses = cn(
        'fun-size-full fun-resize-none fun-appearance-none fun-bg-transparent fun-align-middle fun-font-ui fun-text-base fun-font-light fun-outline-none fun-transition',
        !mutedTextColor && 'fun-text-black fun-placeholder-gray-500',
    );
</script>

<div class="fun-relative fun-box-border fun-flex fun-h-32 fun-items-center sm:fun-h-40">
    {#if showEmoji}
        <u
            class="fun-absolute fun-left-4 fun-top-5 fun-flex fun-h-5 fun-select-none fun-items-center fun-no-underline"
            aria-hidden="true"
        >
            <Emoji {emoji} size="20px" />
        </u>
    {/if}

    <StatusIcon {showError} {showSuccess} showCheckmark={false} />

    <div
        class={textareaContainerClasses}
        style:background-color={backgroundColor}
        style:border-color={!active ? borderColor : undefined}
        style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
    >
        <textarea
            class={textareaClasses}
            class:themed-placeholder={!!mutedTextColor}
            style:color={textColor}
            style:--placeholder-color={mutedTextColor}
            {placeholder}
            name={finalFormInput.name}
            aria-label={placeholder}
            on:blur={finalFormInput.onBlur}
            on:focus={finalFormInput.onFocus}
            on:input={(e) => finalFormInput.onChange(e.currentTarget.value)}
            >{finalFormInput.value ? finalFormInput.value : ''}</textarea
        >

        {#if errorMessage && showError}
            <div
                class="fun-absolute fun-bottom-4 fun-font-ui fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600"
            >
                {errorMessage}
            </div>
        {/if}
    </div>
</div>

<style>
    textarea.themed-placeholder::placeholder {
        color: var(--placeholder-color);
    }
</style>
